import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { type DeliveryFrequency, type FileFormat, type FileEncoding, isSubscription } from '@/lib/pricing';

export interface CartItem {
  id: string; // unique cart item id
  productId: string;
  productName: string;
  productSlug: string;
  basePrice: number; // the variant price for the selected frequency
  currency: string;
  deliveryFrequency: DeliveryFrequency;
  fileFormat: FileFormat;
  fileEncoding: FileEncoding;
  country: string;
  sku: string | null;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'id' | 'quantity'>) => void;
  removeItem: (id: string) => void;
  updateItemQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  subtotal: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'postnummerservice_cart';

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window === 'undefined') return [];
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return [];
    try {
      const parsed = JSON.parse(stored) as CartItem[];
      // Migrate old items that may lack 'country'
      return parsed.map((item) => ({
        ...item,
        country: item.country || 'SE',
      }));
    } catch {
      return [];
    }
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (newItem: Omit<CartItem, 'id' | 'quantity'>) => {
    setItems((prev) => {
      // Check if same product with same config already exists
      const existingIndex = prev.findIndex(
        (item) =>
          item.productId === newItem.productId &&
          item.deliveryFrequency === newItem.deliveryFrequency &&
          item.fileFormat === newItem.fileFormat &&
          item.fileEncoding === newItem.fileEncoding &&
          item.country === newItem.country
      );

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1,
        };
        return updated;
      }

      return [
        ...prev,
        {
          ...newItem,
          id: crypto.randomUUID(),
          quantity: 1,
        },
      ];
    });
  };

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateItemQuantity = (id: string, quantity: number) => {
    if (quantity < 1) {
      removeItem(id);
      return;
    }
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Price is already the variant price — no multiplier needed
  const subtotal = items.reduce((sum, item) => sum + item.basePrice * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateItemQuantity,
        clearCart,
        itemCount,
        subtotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
